// Renders a share-slip payload (see shareSlipUtils.js) to an offscreen canvas
// as a social-ready PNG, in three formats:
//
//   '16:9' — 1200x675  (X/Twitter card)
//   '4:5'  — 1080x1350 (Instagram feed)
//   '9:16' — 1080x1920 (Instagram story — its own centered layout, not a
//            letterboxed card)
//
// The card follows the site's light-mode brand: white surface, brand navy
// (#343b86 / #272d58), Poppins, grey-500 tiles.
//
// Everything drawn here MUST be same-origin: the bundled logo asset,
// self-hosted fonts, and text. cdn.4casters.io serves team logos WITHOUT CORS
// headers, so drawing them would taint the canvas and make toBlob() throw —
// that's why the card is deliberately brand/text-driven. If the CDN ever sends
// Access-Control-Allow-Origin, team logos can be added with crossOrigin images.
import logoUrl from '@/assets/logo.png';
import { formatShareAmount } from '@/core/shareSlipUtils';

// 2x raster so the PNG stays crisp when platforms re-compress it.
const SCALE = 2;

export const SHARE_CARD_FORMATS = {
  '16:9': { width: 1200, height: 675, label: 'X 16:9' },
  '4:5': { width: 1080, height: 1350, label: 'Feed 4:5' },
  '9:16': { width: 1080, height: 1920, label: 'Story 9:16' },
};

const COLORS = {
  background: '#ffffff',
  border: '#dbdfe4',
  heading: '#272d58', // blue-1100 — primary text
  brand: '#343b86', // color-primary
  muted: '#888888', // grey-2400
  separator: '#c4c4c4',
  tileBackground: '#f8f8f8', // grey-500
  tileBorder: '#dbdfe4',
  win: '#009f25',
  loss: '#b32300',
  push: '#f59e0b',
  badgeWinBackground: '#1e5e2e',
  badgeLossBackground: '#b32300',
  badgePushBackground: '#f59e0b',
};

// Site typography is Roboto Slab (300/400/700) — the card matches it.
const FONTS = {
  bold: '700',
  medium: '400',
  regular: '300',
};

let logoPromise = null;

function loadLogo() {
  if (!logoPromise) {
    logoPromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      // Card still renders fine without the mark; never block on a broken asset.
      img.onerror = () => resolve(null);
      img.src = logoUrl;
    });
  }
  return logoPromise;
}

async function ensureFonts() {
  if (!document?.fonts?.load) return;
  try {
    await Promise.all([
      document.fonts.load(`${FONTS.bold} 16px 'Roboto Slab'`),
      document.fonts.load(`${FONTS.medium} 16px 'Roboto Slab'`),
      document.fonts.load(`${FONTS.regular} 16px 'Roboto Slab'`),
    ]);
  } catch {
    // Fall back to whatever the browser substitutes.
  }
}

function font(size, weight) {
  return `${weight} ${size}px 'Roboto Slab', serif`;
}

// The bundled logo is white-on-transparent (made for the navy header), so on
// a white card it must be recolored. source-in compositing stamps the fill
// color through the logo's alpha channel.
function tintLogo(logo, color) {
  const off = document.createElement('canvas');
  off.width = logo.width;
  off.height = logo.height;
  const ctx = off.getContext('2d');
  ctx.drawImage(logo, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, off.width, off.height);
  return off;
}

// Shrinks the font size until the text fits maxWidth (floor at minSize).
function fitFontSize(ctx, text, weight, maxWidth, startSize, minSize) {
  let size = startSize;
  while (size > minSize) {
    ctx.font = font(size, weight);
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 2;
  }
  return size;
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    return;
  }
  // Manual path for older engines without CanvasRenderingContext2D.roundRect.
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function drawFrame(ctx, width, height) {
  ctx.fillStyle = COLORS.background;
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 2.5;
  drawRoundedRect(ctx, 4, 4, width - 8, height - 8, 26);
  ctx.stroke();
}

// One compact line: TYPE · MATCHUP · DATE — the type in brand navy bold, the
// rest muted. Shrinks to fit; alignment 'left' or 'center'.
function drawMetaLine(ctx, segments, { x, y, maxWidth, align, startSize }) {
  const parts = segments.filter((s) => s && s.text);
  if (!parts.length) return;
  const sep = '  ·  ';

  let size = startSize;
  const totalWidth = (s) => {
    let w = 0;
    parts.forEach((p, i) => {
      ctx.font = font(s, p.bold ? FONTS.bold : FONTS.medium);
      w += ctx.measureText(p.text).width;
      if (i < parts.length - 1) {
        ctx.font = font(s, FONTS.medium);
        w += ctx.measureText(sep).width;
      }
    });
    return w;
  };
  while (size > 16 && totalWidth(size) > maxWidth) size -= 1;

  let cursor = align === 'center' ? x - totalWidth(size) / 2 : x;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  parts.forEach((p, i) => {
    ctx.font = font(size, p.bold ? FONTS.bold : FONTS.medium);
    ctx.fillStyle = p.color;
    ctx.fillText(p.text, cursor, y);
    cursor += ctx.measureText(p.text).width;
    if (i < parts.length - 1) {
      ctx.font = font(size, FONTS.medium);
      ctx.fillStyle = COLORS.separator;
      ctx.fillText(sep, cursor, y);
      cursor += ctx.measureText(sep).width;
    }
  });
}

function metaSegments(slip, headline) {
  const isGraded = Boolean(slip?.outcome);
  const typeLabel = (
    slip?.typeLabel || (isGraded ? 'GRADED' : 'BET SLIP')
  ).toUpperCase();
  const matchup = (slip?.matchup || '').toUpperCase();
  const segments = [{ text: typeLabel, color: COLORS.brand, bold: true }];
  // Skip the matchup when the headline already IS the matchup (graded
  // aggregates) so the card doesn't repeat itself.
  if (matchup && matchup !== (headline || '').toUpperCase()) {
    segments.push({ text: matchup, color: COLORS.muted });
  }
  if (slip?.timeLabel) {
    segments.push({ text: slip.timeLabel.toUpperCase(), color: COLORS.muted });
  }
  return segments;
}

// Draws the pick headline, shrinking then wrapping to two lines when needed.
// Returns the y position below the drawn text.
function drawPick(ctx, pick, { x, y, maxWidth, startSize, minSize, align }) {
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.heading;

  const size = fitFontSize(ctx, pick, FONTS.bold, maxWidth, startSize, minSize);
  ctx.font = font(size, FONTS.bold);

  const drawLine = (text, lineY) => {
    const w = Math.min(ctx.measureText(text).width, maxWidth);
    const lineX = align === 'center' ? x - w / 2 : x;
    ctx.textAlign = 'left';
    ctx.fillText(text, lineX, lineY, maxWidth);
  };

  if (ctx.measureText(pick).width <= maxWidth) {
    drawLine(pick, y);
    return y;
  }

  // Still too wide at the minimum size — split across two lines on a space.
  const words = pick.split(' ');
  let firstLine = '';
  let secondLine = '';
  for (const word of words) {
    const candidate = firstLine ? `${firstLine} ${word}` : word;
    if (!secondLine && ctx.measureText(candidate).width <= maxWidth) {
      firstLine = candidate;
    } else {
      secondLine = secondLine ? `${secondLine} ${word}` : word;
    }
  }
  drawLine(firstLine, y);
  drawLine(secondLine, y + size + 12);
  return y + size + 12;
}

function drawStatTile(
  ctx,
  { x, y, width, height, label, value, valueColor, centered },
) {
  ctx.fillStyle = COLORS.tileBackground;
  drawRoundedRect(ctx, x, y, width, height, 16);
  ctx.fill();
  ctx.strokeStyle = COLORS.tileBorder;
  ctx.lineWidth = 2;
  drawRoundedRect(ctx, x, y, width, height, 16);
  ctx.stroke();

  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = centered ? 'center' : 'left';
  const textX = centered ? x + width / 2 : x + 28;

  ctx.fillStyle = COLORS.muted;
  ctx.font = font(Math.round(height * 0.19), FONTS.medium);
  ctx.fillText(label, textX, y + height * 0.34);

  ctx.fillStyle = valueColor || COLORS.heading;
  const size = fitFontSize(
    ctx,
    value,
    FONTS.bold,
    width - 56,
    Math.round(height * 0.4),
    24,
  );
  ctx.font = font(size, FONTS.bold);
  ctx.fillText(value, textX, y + height * 0.8);
  ctx.textAlign = 'left';
}

function drawStatTiles(
  ctx,
  slip,
  { x, y, tileWidth, tileHeight, gap, centered },
) {
  drawStatTile(ctx, {
    x,
    y,
    width: tileWidth,
    height: tileHeight,
    label: 'RISK',
    value: `$${formatShareAmount(slip?.risk)}`,
    centered,
  });
  drawStatTile(ctx, {
    x: x + tileWidth + gap,
    y,
    width: tileWidth,
    height: tileHeight,
    label: 'TO WIN',
    value: `$${formatShareAmount(slip?.win)}`,
    valueColor: COLORS.win,
    centered,
  });
}

// WON/LOST/PUSH pill + net amount. Returns nothing; draws around (x, y) with
// the given alignment. `slip.outcomeLabel` overrides the pill text for results
// that aren't settled bets (e.g. a hedged position that is LOCKED, not won).
function drawOutcomeRow(ctx, slip, { x, y, badgeSize, amountSize, align }) {
  const outcome = slip.outcome;
  const label =
    slip.outcomeLabel ||
    (outcome === 'win' ? 'WON' : outcome === 'loss' ? 'LOST' : 'PUSH');
  const badgeBackground =
    outcome === 'win'
      ? COLORS.badgeWinBackground
      : outcome === 'loss'
        ? COLORS.badgeLossBackground
        : COLORS.badgePushBackground;
  const amount = Number(slip.amount) || 0;
  const amountLabel =
    outcome === 'push'
      ? ''
      : `${amount > 0 ? '+' : '-'}$${formatShareAmount(Math.abs(amount))}`;
  const amountColor =
    outcome === 'win'
      ? COLORS.win
      : outcome === 'loss'
        ? COLORS.loss
        : COLORS.push;

  ctx.font = font(badgeSize, FONTS.bold);
  const badgePaddingX = badgeSize;
  const badgeWidth = ctx.measureText(label).width + badgePaddingX * 2;
  const badgeHeight = badgeSize * 2;
  ctx.font = font(amountSize, FONTS.bold);
  const amountWidth = amountLabel ? ctx.measureText(amountLabel).width : 0;
  const gap = amountLabel ? 34 : 0;
  const totalWidth = badgeWidth + gap + amountWidth;
  const startX = align === 'center' ? x - totalWidth / 2 : x;
  const centerY = y;

  ctx.fillStyle = badgeBackground;
  drawRoundedRect(
    ctx,
    startX,
    centerY - badgeHeight / 2,
    badgeWidth,
    badgeHeight,
    badgeHeight / 2,
  );
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = font(badgeSize, FONTS.bold);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, startX + badgePaddingX, centerY + 2);

  if (amountLabel) {
    ctx.fillStyle = amountColor;
    ctx.font = font(amountSize, FONTS.bold);
    ctx.fillText(amountLabel, startX + badgeWidth + gap, centerY + 2);
  }
  ctx.textBaseline = 'alphabetic';
}

// Header used by the landscape and feed layouts: navy mark + wordmark on the
// left, league on the right.
function drawHeader(
  ctx,
  logo,
  league,
  { margin, y, logoHeight, wordmarkSize, width },
) {
  let textX = margin;
  if (logo) {
    const mark = tintLogo(logo, COLORS.brand);
    const logoWidth = (mark.width / mark.height) * logoHeight;
    ctx.drawImage(mark, margin, y, logoWidth, logoHeight);
    textX = margin + logoWidth + 22;
  }
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = COLORS.heading;
  ctx.font = font(wordmarkSize, FONTS.bold);
  ctx.fillText('4casters', textX, y + logoHeight / 2 + 2);

  if (league) {
    ctx.textAlign = 'right';
    ctx.fillStyle = COLORS.muted;
    ctx.font = font(Math.round(wordmarkSize * 0.66), FONTS.medium);
    ctx.fillText(
      String(league).toUpperCase(),
      width - margin,
      y + logoHeight / 2 + 2,
    );
    ctx.textAlign = 'left';
  }
  ctx.textBaseline = 'alphabetic';
}

function drawBottomBlock(
  ctx,
  slip,
  {
    isGraded,
    x,
    y,
    tileWidth,
    tileHeight,
    gap,
    badgeSize,
    amountSize,
    centered,
  },
) {
  if (isGraded) {
    drawOutcomeRow(ctx, slip, {
      x: centered ? x : x,
      y: y + tileHeight / 2,
      badgeSize,
      amountSize,
      align: centered ? 'center' : 'left',
    });
  } else {
    const startX = centered ? x - tileWidth - gap / 2 : x;
    drawStatTiles(ctx, slip, {
      x: startX,
      y,
      tileWidth,
      tileHeight,
      gap,
      centered,
    });
  }
}

// --- Layouts ---------------------------------------------------------------

function layoutLandscape(ctx, slip, logo) {
  const { width, height } = SHARE_CARD_FORMATS['16:9'];
  const margin = 80;
  drawFrame(ctx, width, height);
  drawHeader(ctx, logo, slip?.league, {
    margin,
    y: 58,
    logoHeight: 60,
    wordmarkSize: 38,
    width,
  });
  const pick = slip?.pick || slip?.matchup || '';
  drawMetaLine(ctx, metaSegments(slip, pick), {
    x: margin,
    y: 250,
    maxWidth: width - margin * 2,
    align: 'left',
    startSize: 26,
  });
  drawPick(ctx, pick, {
    x: margin,
    y: 356,
    maxWidth: width - margin * 2,
    startSize: 80,
    minSize: 44,
    align: 'left',
  });
  drawBottomBlock(ctx, slip, {
    isGraded: Boolean(slip?.outcome),
    x: margin,
    y: 452,
    tileWidth: 330,
    tileHeight: 132,
    gap: 24,
    badgeSize: 34,
    amountSize: 66,
    centered: false,
  });
}

function layoutFeed(ctx, slip, logo) {
  const { width, height } = SHARE_CARD_FORMATS['4:5'];
  const margin = 80;
  drawFrame(ctx, width, height);
  drawHeader(ctx, logo, slip?.league, {
    margin,
    y: 76,
    logoHeight: 66,
    wordmarkSize: 40,
    width,
  });
  const pick = slip?.pick || slip?.matchup || '';
  drawMetaLine(ctx, metaSegments(slip, pick), {
    x: margin,
    y: 420,
    maxWidth: width - margin * 2,
    align: 'left',
    startSize: 28,
  });
  drawPick(ctx, pick, {
    x: margin,
    y: 546,
    maxWidth: width - margin * 2,
    startSize: 92,
    minSize: 50,
    align: 'left',
  });
  drawBottomBlock(ctx, slip, {
    isGraded: Boolean(slip?.outcome),
    x: margin,
    y: 940,
    tileWidth: 448,
    tileHeight: 156,
    gap: 24,
    badgeSize: 40,
    amountSize: 84,
    centered: false,
  });
}

// The story layout is its own centered composition sized for a full-screen
// 9:16 canvas, keeping IG's top/bottom UI zones clear — not the landscape
// card floating in empty space.
function layoutStory(ctx, slip, logo) {
  const { width, height } = SHARE_CARD_FORMATS['9:16'];
  const centerX = width / 2;
  drawFrame(ctx, width, height);

  if (logo) {
    const mark = tintLogo(logo, COLORS.brand);
    const markHeight = 150;
    const markWidth = (mark.width / mark.height) * markHeight;
    ctx.drawImage(mark, centerX - markWidth / 2, 330, markWidth, markHeight);
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = COLORS.heading;
  ctx.font = font(54, FONTS.bold);
  ctx.fillText('4casters', centerX, 580);
  if (slip?.league) {
    ctx.fillStyle = COLORS.muted;
    ctx.font = font(32, FONTS.medium);
    ctx.fillText(String(slip.league).toUpperCase(), centerX, 640);
  }
  ctx.textAlign = 'left';

  const pick = slip?.pick || slip?.matchup || '';
  drawMetaLine(ctx, metaSegments(slip, pick), {
    x: centerX,
    y: 840,
    maxWidth: width - 180,
    align: 'center',
    startSize: 30,
  });
  drawPick(ctx, pick, {
    x: centerX,
    y: 990,
    maxWidth: width - 180,
    startSize: 100,
    minSize: 54,
    align: 'center',
  });
  drawBottomBlock(ctx, slip, {
    isGraded: Boolean(slip?.outcome),
    x: centerX,
    y: 1240,
    tileWidth: 430,
    tileHeight: 180,
    gap: 36,
    badgeSize: 46,
    amountSize: 100,
    centered: true,
  });
}

const LAYOUTS = {
  '16:9': layoutLandscape,
  '4:5': layoutFeed,
  '9:16': layoutStory,
};

/**
 * Renders the slip to a canvas in the requested format ('16:9' | '4:5' |
 * '9:16'), drawn at 2x for crisp re-compression.
 */
export async function renderShareCard(slip, format = '16:9') {
  const spec = SHARE_CARD_FORMATS[format] || SHARE_CARD_FORMATS['16:9'];
  const layout = LAYOUTS[format] || layoutLandscape;
  const [logo] = await Promise.all([loadLogo(), ensureFonts()]);

  const canvas = document.createElement('canvas');
  canvas.width = spec.width * SCALE;
  canvas.height = spec.height * SCALE;
  const ctx = canvas.getContext('2d');
  ctx.scale(SCALE, SCALE);

  layout(ctx, slip, logo);
  return canvas;
}

/** Renders the slip and resolves to a PNG Blob. */
export async function renderShareCardBlob(slip, format = '16:9') {
  const canvas = await renderShareCard(slip, format);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Share card export failed'));
    }, 'image/png');
  });
}
